import { tsFromISO } from '../../config/chartTheme';
import type { TourismDay } from '../../api/tourism';

/**
 * Eine Zeile je Kalendertag — die gemeinsame Datengrundlage aller drei
 * Zeitdiagramme.
 *
 * Warum zentral und warum lückenlos:
 *
 * 1. **Lücken müssen Lücken bleiben.** Tage, für die die Plattform nie etwas
 *    gemeldet hat, fehlen in der API-Antwort. Ließe man sie einfach weg, zöge
 *    d3 eine gerade Linie über den Ausfall hinweg, als wäre nichts gewesen.
 *    Sie bekommen deshalb eine Zeile mit `null`-Werten; die Diagramme setzen
 *    **kein** `connectNulls`.
 * 2. **Die Kopplung verlangt es.** `syncMethod` ist standardmäßig `index`, und
 *    die Brush-Kopplung ist immer index-basiert. Nur wenn alle Diagramme
 *    dasselbe, gleich lange, gleich sortierte Array bekommen, zeigen Tooltip
 *    und Zoom überall denselben Tag.
 */
export interface TimeRow {
  /** Zeitstempel (UTC-Mitternacht) — der `dataKey` der Zeitachse. */
  ts: number;
  iso: string;
  isFuture: boolean;
  /** Belegung in Prozent, aufgeteilt für die durchgezogene/gestrichelte Linie. */
  observedPct: number | null;
  announcedPct: number | null;
  referencePct: number | null;
  /** Inserate mit Angabe an diesem Tag (Auswahl bzw. Gesamtstadt). */
  n: number | null;
  referenceN: number | null;
  signalsPast: number | null;
  signalsFuture: number | null;
}

const DAY_MS = 86_400_000;

function pct(unavailable: number, known: number): number | null {
  return known > 0 ? Math.round((unavailable / known) * 1000) / 10 : null;
}

function addDays(iso: string, days: number): string {
  return new Date(tsFromISO(iso) + days * DAY_MS).toISOString().slice(0, 10);
}

export function buildTimeRows(series: TourismDay[], todayISO: string): TimeRow[] {
  if (series.length === 0) return [];

  const byDate = new Map(series.map(point => [point.date, point]));
  const first = series[0].date;
  const last = series[series.length - 1].date;

  const rows: TimeRow[] = [];
  let iso = first;
  let boundaryDone = false;

  while (iso <= last) {
    const point = byDate.get(iso);
    const isFuture = iso > todayISO;

    if (!point) {
      // Kein Eintrag = die Plattform hat für diesen Tag nie etwas gemeldet.
      rows.push({
        ts: tsFromISO(iso), iso, isFuture,
        observedPct: null, announcedPct: null, referencePct: null,
        n: null, referenceN: null, signalsPast: null, signalsFuture: null,
      });
      iso = addDays(iso, 1);
      continue;
    }

    const value = pct(point.selection.unavailable, point.selection.known);
    // Der erste Zukunftstag steht in BEIDEN Serien, sonst klafft zwischen
    // durchgezogener und gestrichelter Linie eine Lücke von einem Tag.
    const isBoundary = isFuture && !boundaryDone;
    if (isBoundary) boundaryDone = true;

    rows.push({
      ts: tsFromISO(iso),
      iso,
      isFuture,
      observedPct: !isFuture || isBoundary ? value : null,
      announcedPct: isFuture ? value : null,
      referencePct: pct(point.reference.unavailable, point.reference.known),
      n: point.selection.known,
      referenceN: point.reference.known,
      signalsPast: isFuture ? null : point.selection.signals,
      signalsFuture: isFuture ? point.selection.signals : null,
    });
    iso = addDays(iso, 1);
  }

  return rows;
}

/** Wie `buildTimeRows`, aber die Hauptkurve zeigt die Gesamtstadt. */
export function buildReferenceRows(series: TourismDay[], todayISO: string): TimeRow[] {
  const mirrored = series.map(point => ({ ...point, selection: point.reference }));
  return buildTimeRows(mirrored, todayISO);
}

/** Erster Zukunftstag als Zeitstempel — Startpunkt der Zukunfts-Schattierung. */
export function firstFutureTs(rows: TimeRow[]): number | null {
  return rows.find(row => row.isFuture)?.ts ?? null;
}
